import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { DynamicIcon } from 'src/components';

const GlassBorderWrap = styled(Box)(({ theme }) => {
  const secondary = theme.palette.custom.OV.sys.secondary;
  const outline = theme.palette.custom.OV.sys.outlineVariant;

  return {
    position: 'fixed',
    bottom: '1.5rem',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    borderRadius: '6.25rem',
    padding: '0.0625rem', // 1 px border
    background: `linear-gradient(135deg, ${secondary}80, ${outline}40)`,
    boxShadow: `0 0.5rem 2rem ${theme.palette.custom.OV.sys.scrim}33`,
  };
});

const GlassContent = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '0.75rem',
  borderRadius: 'inherit',
  padding: '0.375rem 0.5rem 0.375rem 1.25rem',
  minWidth: '17rem',
  justifyContent: 'space-between',
  backdropFilter: 'blur(1.25rem) saturate(180%)',
  WebkitBackdropFilter: 'blur(1.25rem) saturate(180%)',
  backgroundColor: `${theme.palette.custom.OV.sys.surfaceContainerHigh}B3`,
}));

/** Gradient border wrapper for the actions button — same trick as GlassBorderWrap. */
const ActionsBorderWrap = styled(Box)(({ theme }) => {
  const secondary = theme.palette.custom.OV.sys.secondary;
  const outline = theme.palette.custom.OV.sys.outlineVariant;
  return {
    zIndex: 1,
    borderRadius: '6.25rem',
    padding: '0.0625rem', // 1 px border
    background: `linear-gradient(135deg, ${secondary}80, ${outline}40)`,
  };
});

const ActionsButton = styled(Button)(({ theme }) => {
  const surface = theme.palette.custom.OV.sys.surfaceContainerHigh;
  const secondary = theme.palette.custom.OV.sys.secondary;
  return {
    borderRadius: 'inherit',
    textTransform: 'none',
    fontWeight: 600,
    paddingLeft: '1rem',
    paddingRight: '1.25rem',
    gap: '0.375rem',
    // Opaque gradient so the border-wrapper gradient doesn't bleed through
    background: `linear-gradient(135deg, ${surface}, color-mix(in srgb, ${secondary} 30%, ${surface}))`,
    color: theme.palette.custom.OV.sys.onSurface,
    boxShadow: 'none',
    '&:hover': {
      background: `linear-gradient(135deg, ${surface}, color-mix(in srgb, ${secondary} 45%, ${surface}))`,
      boxShadow: 'none',
    },
  };
});

type BulkActionBarProps = {
  selectionSummary: string;
  onActionsClick: () => void;
  onClearClick: () => void;
  isActionsLoading?: boolean;
};

export const BulkActionBar = ({
  selectionSummary,
  onActionsClick,
  onClearClick,
  isActionsLoading = false,
}: BulkActionBarProps) => (
  <GlassBorderWrap>
    <GlassContent>
      <Typography
        variant="body2"
        noWrap
        sx={(theme) => ({
          fontWeight: 500,
          color: theme.palette.custom.OV.sys.onSurface,
        })}
      >
        {selectionSummary}
      </Typography>
      <IconButton
        size="small"
        onClick={onClearClick}
        sx={(theme) => ({
          color: theme.palette.custom.OV.sys.onSurfaceVariant,
          '&:hover': { color: theme.palette.custom.OV.sys.onSurface },
        })}
      >
        <DynamicIcon name="close" fontSize="small" />
      </IconButton>
      <Divider
        orientation="vertical"
        flexItem
        sx={(theme) => ({
          my: '0.25rem',
          borderColor: theme.palette.custom.OV.sys.outlineVariant,
        })}
      />
      <ActionsBorderWrap>
        <ActionsButton
          size="small"
          variant="contained"
          onClick={onActionsClick}
          disabled={isActionsLoading}
        >
          {isActionsLoading ? (
            <CircularProgress color="inherit" size={16} />
          ) : (
            <DynamicIcon name="auto_awesome" sx={{ fontSize: '1.125rem' }} />
          )}
          {isActionsLoading ? 'Copying markdown...' : 'Actions'}
        </ActionsButton>
      </ActionsBorderWrap>
    </GlassContent>
  </GlassBorderWrap>
);
